import { useState, useEffect } from 'react';
import { Layout } from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { FileText, Download, Sparkles } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';

export default function Reports({ user, onLogout }) {
  const [report, setReport] = useState('');
  const [loading, setLoading] = useState(false);
  const [reportType, setReportType] = useState('');

  const generateReport = async (type) => {
    setLoading(true);
    setReportType(type);

    try {
      const prompts = {
        monthly: 'Generate a comprehensive monthly business report with key metrics, trends, and recommendations.',
        financial: 'Generate a detailed financial report covering revenue, expenses, and profitability analysis.',
        leads: 'Generate a leads performance report with conversion rates and recommendations for improvement.',
        custom: 'Generate an executive summary report covering all aspects of the business.'
      };

      const response = await api.post('/ai/report', {
        prompt: prompts[type] || prompts.custom
      });

      setReport(response.data.report);
      toast.success('Report generated successfully!');
    } catch (error) {
      toast.error('Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const downloadReport = () => {
    const element = document.createElement('a');
    const file = new Blob([report], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = `${reportType}-report-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
    toast.success('Report downloaded!');
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6 max-w-5xl mx-auto" data-testid="reports-page">
        <Card data-testid="report-generator-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="text-amber-600" />
              AI Report Generator
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              <Button
                onClick={() => generateReport('monthly')}
                disabled={loading}
                data-testid="generate-monthly-report"
                className="bg-emerald-600 hover:bg-emerald-700 h-auto py-4 flex flex-col items-center gap-2"
              >
                <FileText size={24} />
                <span>Monthly Report</span>
              </Button>

              <Button
                onClick={() => generateReport('financial')}
                disabled={loading}
                data-testid="generate-financial-report"
                className="bg-blue-600 hover:bg-blue-700 h-auto py-4 flex flex-col items-center gap-2"
              >
                <FileText size={24} />
                <span>Financial Report</span>
              </Button>

              <Button
                onClick={() => generateReport('leads')}
                disabled={loading}
                data-testid="generate-leads-report"
                className="bg-purple-600 hover:bg-purple-700 h-auto py-4 flex flex-col items-center gap-2"
              >
                <FileText size={24} />
                <span>Leads Report</span>
              </Button>

              <Button
                onClick={() => generateReport('custom')}
                disabled={loading}
                data-testid="generate-custom-report"
                className="bg-amber-600 hover:bg-amber-700 h-auto py-4 flex flex-col items-center gap-2"
              >
                <FileText size={24} />
                <span>Executive Summary</span>
              </Button>
            </div>
          </CardContent>
        </Card>

        {loading && (
          <Card>
            <CardContent className="py-12">
              <div className="flex flex-col items-center gap-4">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
                <p className="text-slate-600">Generating report with AI...</p>
              </div>
            </CardContent>
          </Card>
        )}

        {report && !loading && (
          <Card data-testid="report-result-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Generated Report</CardTitle>
                <Button
                  onClick={downloadReport}
                  data-testid="download-report-button"
                  variant="outline"
                  className="gap-2"
                >
                  <Download size={18} />
                  Download
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                <pre className="whitespace-pre-wrap font-sans text-sm text-slate-700 leading-relaxed" data-testid="report-content">
                  {report}
                </pre>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
